import { Button } from '@/components/ui/button';
import { usePayment } from '@/context/PaymentContext';
import { SEO } from '@/components/SEO';
import { useTheme } from '@/context/ThemeContext';
import { PLAN_PRICING } from '@/lib/pricing';
import { cn } from '@/lib/utils';
import { useState, type MouseEvent } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

type BillingPeriod = 'monthly' | 'yearly';

export function PricingPage() {
  const { openPaymentModal } = usePayment();
  const { isDarkMode } = useTheme();
  const overlayBg = isDarkMode ? 'rgba(6, 9, 18, 0.82)' : 'rgba(220, 232, 255, 0.45)';
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const isYearly = billingPeriod === 'yearly';

  const cardBase = 'relative rounded-2xl p-6 border transition-all';
  const cardSurface = 'bg-card border-border text-card-foreground shadow-sm';
  const cardSurfaceDark = 'dark:bg-slate-800/30 dark:border-slate-700/50 dark:text-white';
  const softCardSurface = 'bg-white border-border text-card-foreground shadow-sm';
  const softCardSurfaceDark = 'dark:bg-slate-800/30 dark:border-slate-700/50 dark:text-white';

  const planDefs = [
    {
      key: 'starter',
      name: 'Starter',
      features: [
        '10 high-fit investor matches',
        'Limited insights',
        'Basic filtering',
        'Warm intros',
        '50 AI messages/month',
      ],
      crmGoal: 'Workspace tracking is not included on Starter.',
      crmCapabilities: [] as string[],
      crmFeeling: 'Starter is for investor discovery only.',
      crmLimitation: 'No pipeline, no notes, no follow-up tracking, and no workspace tracking on this tier.',
      aiGoal: 'Get answers fast — no fluff.',
      aiCapabilities: [
        '50 AI messages per month',
        'Fundraising Q&A and strategy coaching',
        'Basic email and pitch copy suggestions',
      ] as string[],
      aiFeeling: 'Enough AI to get oriented and unblocked.',
      cta: 'Start Cheap',
      popular: false,
    },
    {
      key: 'fundraise',
      name: 'Fundraise Plan',
      features: [
        '25-50 high-fit investor matches',
        'Detailed "why this investor" insights',
        'Filtering by stage, check size, and industry',
        'Export and outreach support',
        'Investor network & pipeline',
        '300 AI messages/month',
      ],
      crmGoal: 'Turn outreach into a system, not guesswork.',
      crmCapabilities: [
        'Smart pipeline with custom stages: Not contacted, Sent, Opened, Replied, Meeting booked, Passed',
        'Follow-up intelligence with automatic reminders and suggested timing',
        'Interaction tracking: email sent logging, response tracking, notes per interaction',
        'Match context in your workspace: why fit, recent deals, stage, check size',
        'Basic analytics: reply rate, meetings booked, outreach volume',
      ],
      crmFeeling: 'Connects data directly to action so founders can run repeatable outreach.',
      crmLimitation: '',
      aiGoal: 'Your AI fundraising advisor, always on.',
      aiCapabilities: [
        '300 AI messages per month',
        'Full CRM context — AI sees your contacts, deals, and pipeline',
        'Personalized investor email drafts',
        'Follow-up timing and outreach strategy suggestions',
        'Interprets signals from your investor interactions',
      ] as string[],
      aiFeeling: 'Like having a fundraising advisor available 24/7, with full context on your round.',
      cta: 'Choose Fundraise Plan',
      popular: true,
    },
    {
      key: 'pro',
      name: 'Pro Plan',
      features: [
        '100+ high-fit investor matches',
        'Advanced filters',
        'Priority and "hot investor" signals',
        'Ongoing updates with new investors added weekly',
        'Investor network & pipeline',
        'Unlimited AI messages',
      ],
      crmGoal: 'Maximize conversion and speed for active fundraises.',
      crmCapabilities: [
        'Priority signal tracking: actively investing now, recently funded competitors, new fund announcements',
        'Outreach optimization: best-performing message tracking, simple A/B email tracking, suggested improvements',
        'Advanced pipeline views filtered by likelihood to respond, stage fit, and engagement level',
        'Relationship layer: warm vs cold, referral paths, and mutual connections',
        'Ongoing investor feed: weekly new matches and updates on existing investors',
      ],
      crmFeeling: 'Feels like a real edge when speed and conversion matter most.',
      crmLimitation: '',
      aiGoal: 'Unlimited AI when every conversation counts.',
      aiCapabilities: [
        'Unlimited AI messages — no monthly cap',
        'Full CRM + investor signal context',
        'Advanced analysis: timing, engagement patterns, likely to close',
        'AI-assisted prioritization of your hottest prospects',
        'Ongoing investor feed fed directly into AI context',
      ] as string[],
      aiFeeling: 'Maximum edge when speed and intelligence matter most.',
      cta: 'Go Pro',
      popular: false,
    },
  ];

  // Resolve the pricing.ts entry for the current billing period — this is the single
  // source of truth for prices. Edit `pricing.ts` to change what's displayed here.
  const plans = planDefs.map((p) => {
    const pricingKey = isYearly ? (`${p.key}_yearly` as keyof typeof PLAN_PRICING) : (p.key as keyof typeof PLAN_PRICING);
    const pricing = PLAN_PRICING[pricingKey];
    return {
      ...p,
      pricing,
      planKey: pricing.key,
      description: pricing.description,
    };
  });

  const formatMonthlyEquivalent = (yearlyPrice: number) => {
    const monthly = yearlyPrice / 12;
    // Show cents only when not a whole dollar
    return Number.isInteger(monthly) ? `$${monthly}` : `$${monthly.toFixed(2)}`;
  };

  return (
    <>
      <SEO
        title="Pricing - RaiseBeacon"
        description="Choose the plan that matches your fundraising intensity, from first outreach to full pipeline execution."
        canonicalUrl="https://backr.us/#/pricing"
      />
      <div className="min-h-screen relative">

        {/* Hero */}
        <section className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground dark:text-white mb-4">
                Choose the plan that matches how you raise
              </h1>
              <p className="text-muted-foreground dark:text-slate-400 max-w-xl mx-auto">
                Start with structure, scale into a system, and unlock advanced signals when every conversation counts.
              </p>
            </motion.div>

            {/* Billing period toggle */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="mt-8 flex justify-center"
            >
              <div
                role="radiogroup"
                aria-label="Billing period"
                className="relative inline-flex items-center p-1 rounded-full bg-muted border border-border dark:bg-slate-700/70 dark:border-slate-600"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={!isYearly}
                  onClick={() => setBillingPeriod('monthly')}
                  className={cn(
                    'relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors',
                    !isYearly
                      ? 'bg-background text-foreground shadow-sm dark:bg-white dark:text-slate-900'
                      : 'text-muted-foreground dark:text-slate-300'
                  )}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isYearly}
                  onClick={() => setBillingPeriod('yearly')}
                  className={cn(
                    'relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-2',
                    isYearly
                      ? 'bg-background text-foreground shadow-sm dark:bg-white dark:text-slate-900'
                      : 'text-muted-foreground dark:text-slate-300'
                  )}
                >
                  Yearly
                  <span className="text-[11px] font-semibold text-[#2B7FFF] bg-[#2B7FFF]/10 px-2 py-0.5 rounded-full">
                    Save 25%
                  </span>
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Plans */}
        <section className="pb-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={cn(
                    cardBase,
                    plan.popular
                      ? 'bg-white dark:bg-[#2B7FFF]/5 border-[#2B7FFF]/40 scale-105 text-card-foreground dark:text-white shadow-lg dark:shadow-none'
                      : `${cardSurface} ${cardSurfaceDark} hover:border-slate-400 dark:hover:border-slate-600`
                  )}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#2B7FFF] text-white text-xs font-medium px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div className="text-xl font-semibold text-foreground dark:text-white mb-1">
                      {plan.name}
                    </div>
                    <div className="text-muted-foreground dark:text-slate-400 text-sm">{plan.description}</div>
                  </div>

                  <div className="text-center mb-6">
                    {isYearly ? (
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-3xl font-bold text-foreground dark:text-white">
                            {formatMonthlyEquivalent(plan.pricing.price)}
                          </span>
                          <span className="text-muted-foreground dark:text-slate-500 text-sm">/mo</span>
                        </div>
                        <div className="text-xs text-muted-foreground dark:text-slate-400">
                          {plan.pricing.displayPrice} billed yearly
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-bold text-foreground dark:text-white">{plan.pricing.displayPrice}</span>
                        <p className="text-muted-foreground dark:text-slate-500 text-sm mt-2">/mo</p>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-card-foreground dark:text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={(e: MouseEvent) => openPaymentModal(e, plan.planKey)}
                    className={`
                      w-full py-5 font-medium rounded-xl transition-all
                      ${plan.popular
                        ? 'bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white'
                        : 'bg-slate-700 hover:bg-slate-600 text-white'
                      }
                    `}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Workspace capabilities by tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-16"
            >
              <h3 className="text-2xl font-semibold text-foreground dark:text-white text-center mb-8">
                Workspace & pipeline by tier
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <div
                    key={`${plan.name}-workspace`}
                    className={cn(
                      'rounded-2xl border p-6',
                      plan.popular
                        ? 'bg-white dark:bg-[#2B7FFF]/5 border-[#2B7FFF]/40 text-card-foreground dark:text-white shadow-md dark:shadow-none'
                        : `${softCardSurface} ${softCardSurfaceDark}`
                    )}
                  >
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-foreground dark:text-white">{plan.name}</h4>
                      <p className="text-sm text-muted-foreground dark:text-slate-400 mt-1">{plan.crmGoal}</p>
                    </div>
                    {plan.crmCapabilities.length > 0 ? (
                      <ul className="space-y-3">
                        {plan.crmCapabilities.map((capability) => (
                          <li key={capability} className="flex items-start gap-3 text-sm">
                            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-card-foreground dark:text-slate-300">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="rounded-xl border border-dashed border-border bg-background px-4 py-5 text-sm text-card-foreground dark:text-slate-300 dark:border-slate-600/70 dark:bg-slate-900/40">
                        No workspace tracking included. Starter is discovery only.
                      </div>
                    )}
                    <div className="mt-5 pt-5 border-t border-border dark:border-slate-700/60">
                      <p className="text-xs text-muted-foreground dark:text-slate-400">{plan.crmFeeling}</p>
                      {plan.crmLimitation && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">{plan.crmLimitation}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* AI by tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-16"
            >
              <h3 className="text-2xl font-semibold text-foreground dark:text-white text-center mb-2">
                AI assistant by tier
              </h3>
              <p className="text-sm text-muted-foreground dark:text-slate-400 text-center mb-8">
                Every plan includes RaiseBeacon AI. The difference is how much you can use it each month.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <div
                    key={`${plan.name}-ai`}
                    className={cn(
                      'rounded-2xl border p-6',
                      plan.popular
                        ? 'bg-white dark:bg-[#2B7FFF]/5 border-[#2B7FFF]/40 text-card-foreground dark:text-white shadow-md dark:shadow-none'
                        : `${softCardSurface} ${softCardSurfaceDark}`
                    )}
                  >
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-foreground dark:text-white">{plan.name}</h4>
                      <p className="text-sm text-muted-foreground dark:text-slate-400 mt-1">{plan.aiGoal}</p>
                    </div>
                    <ul className="space-y-3">
                      {plan.aiCapabilities.map((capability) => (
                        <li key={capability} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                          <span className="text-card-foreground dark:text-slate-300">{capability}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 pt-5 border-t border-border dark:border-slate-700/60">
                      <p className="text-xs text-muted-foreground dark:text-slate-400">{plan.aiFeeling}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FAQ */}
          </div>
        </section>
      </div>
    </>
  );
}
